import { Capacitor } from '@capacitor/core';

/**
 * Returns the web origin safe for sharing — works on both browser and Capacitor (iOS/Android).
 * On Capacitor, window.location.origin is the WebView's internal origin, not a real domain:
 * iOS defaults to capacitor://localhost, Android defaults to https://localhost (no custom
 * scheme) — neither is reachable from outside the app, so native platforms always need the
 * configured web domain instead. Checking Capacitor.isNativePlatform() directly (rather than
 * pattern-matching origin prefixes) covers both cases and any future scheme changes.
 */
export const getWebOrigin = () => {
  if (Capacitor.isNativePlatform()) {
    return import.meta.env.REACT_APP_MOBILE_BACKEND_DOMAIN || '';
  }
  return window.location.origin;
};

/**
 * Turns a stored link into an absolute, shareable URL — and leaves it alone if
 * it already is one.
 *
 * Callers used to concatenate getWebOrigin() + value unconditionally, which
 * produced a dead link the moment the value arrived absolute:
 *   https://dev.kynaixsports.comhttps://dev.kynaixsports.com/flyers/...
 * Program flyer URLs are stored relative today, but a share link is the one
 * thing a customer pastes elsewhere, so it should not depend on every producer
 * agreeing about that forever.
 */
export const toShareUrl = (pathOrUrl) => {
  if (!pathOrUrl) return '';
  // Any scheme, not just http(s) — a mailto: or capacitor: value is likewise
  // already absolute and must not be prefixed.
  if (/^[a-z][a-z0-9+.-]*:/i.test(pathOrUrl) || pathOrUrl.startsWith('//')) return pathOrUrl;
  return `${getWebOrigin()}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
};

/**
 * Turns a stored image reference into one an <img> can actually load.
 *
 * Two things go wrong without this. A relative `/images/...` has no origin on
 * native — Capacitor serves the app from `capacitor://localhost`, so the
 * browser-correct relative path resolves against the app bundle and the image
 * silently fails while working fine on the web. And not every producer stores
 * the `/images/` prefix: some rows hold a bare S3 key like
 * `academies/<id>/programs/<id>/cover.jpg`, which is broken everywhere, because
 * relative-without-a-slash resolves against whatever route happens to be open.
 *
 * Anything already absolute (including data: and blob:) is returned untouched.
 */
export const resolveMediaUrl = (pathOrUrl) => {
  if (!pathOrUrl) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(pathOrUrl) || pathOrUrl.startsWith('//')) return pathOrUrl;
  // A leading slash means the caller already said which route serves it
  // (/images, /flyers, /files, /permits) — only bare keys are image storage.
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/images/${pathOrUrl}`;
  return `${getWebOrigin()}${path}`;
};

/**
 * Return the view to the top after something finishes off-screen.
 *
 * The window is not always what scrolls: a game can be running inside the
 * fullscreen tournament schedule overlay, which scrolls its own container. So
 * this resets the window and every scrollable ancestor of `el`, and does
 * nothing harmful when there aren't any.
 *
 * @param {Element} [el] an element inside the region that should return to top
 */
export const scrollToTop = (el) => {
  try { window.scrollTo({ top: 0, behavior: 'auto' }); } catch { /* non-DOM env */ }
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollTop > 0) node.scrollTop = 0;
    node = node.parentElement;
  }
};

/**
 * Top padding that clears the status bar and camera cutout.
 *
 * The native branch deliberately contains no env(). Whatever this web view does
 * with safe-area-inset-top, depending on it did not work — plain, then inside a
 * max() floor — and a calc() holding a term the engine rejects is dropped
 * whole, taking the padding to zero. That is precisely the symptom: controls
 * drawn into the cutout, clipped and untappable because the strip is outside
 * the hit-test area. A static value cannot fail that way.
 *
 * 59px is the Dynamic Island, the deepest cutout of any iPhone.
 *
 * @param {string} extra additional spacing below the inset, as a CSS length
 */
export const safeAreaTop = (extra = '0px') =>
  Capacitor.isNativePlatform()
    ? `calc(59px + ${extra})`
    : `calc(env(safe-area-inset-top) + ${extra})`;

/**
 * Splits a stored display name into the first/last pair the avatar and chat
 * header want.
 *
 * Several APIs return people as a single `name` (the academy staff directory
 * and Stream both do) while the UI asks for firstName/lastName — passing the
 * whole object straight through therefore rendered "Unknown" with a blank
 * avatar, since neither field existed on it.
 */
export const splitDisplayName = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName:  parts.slice(1).join(' ') || '',
  };
};

export const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  export const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  export const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  export const calculateWinPercentage = (wins, losses) => {
    const total = wins + losses;
    if (total === 0) return 0;
    return Math.round((wins / total) * 100);
  };

  export const generateRandomId = () => {
    return Math.random().toString(36).substr(2, 9);
  };

/**
 * Converts a UTC date/time to Eastern Time (ET)
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} timeStr - Time string in HH:MM format (24-hour)
 * @returns {Object} - { date: 'YYYY-MM-DD', time: 'HH:MM', displayDate: Date object in ET }
 */
export const convertUTCtoET = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) {
    return { date: dateStr, time: timeStr, displayDate: null };
  }

  // Create a UTC date from the date and time strings
  const utcDateTimeStr = `${dateStr}T${timeStr}:00.000Z`;
  const utcDate = new Date(utcDateTimeStr);

  // Convert to ET using toLocaleString with America/New_York timezone
  const etDateStr = utcDate.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  // Parse the ET string back to get components
  // Format will be: MM/DD/YYYY, HH:MM
  const [datePart, timePart] = etDateStr.split(', ');
  const [month, day, year] = datePart.split('/');
  const etDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  const etTime = timePart;

  // Create a Date object for display purposes (in local timezone)
  const displayDate = new Date(`${etDate}T${etTime}:00`);

  return {
    date: etDate,
    time: etTime,
    displayDate: displayDate
  };
};

/**
 * Converts Eastern Time (ET) to UTC for storage
 * @param {string} dateStr - Date string in YYYY-MM-DD format (in ET)
 * @param {string} timeStr - Time string in HH:MM format (24-hour, in ET)
 * @returns {Object} - { date: 'YYYY-MM-DD', time: 'HH:MM' } in UTC
 */
export const convertETtoUTC = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) {
    return { date: dateStr, time: timeStr };
  }

  // Create a string that represents the ET date/time
  const [year, month, day] = dateStr.split('-');
  const [hour, minute] = timeStr.split(':');

  // Create a locale string for ET
  const etDateString = `${month}/${day}/${year} ${hour}:${minute}:00`;

  // Parse this as a date in ET timezone by using toLocaleString
  // First, create any date object
  const referenceDate = new Date(2024, 0, 1);

  // Get ET offset by comparing UTC and ET representations
  const etString = referenceDate.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  // Now create the desired ET time
  const desiredETTime = new Date(`${year}-${month}-${day}T${hour}:${minute}:00`);

  // Get how this time appears in ET
  const asET = desiredETTime.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  // Parse the ET representation
  const etParts = asET.match(/(\d{2})\/(\d{2})\/(\d{4}),\s(\d{2}):(\d{2}):(\d{2})/);
  if (!etParts) {
    return { date: dateStr, time: timeStr };
  }

  const [, etMonth, etDay, etYear, etHour, etMinute] = etParts;

  // Calculate the difference between what we wanted and what we got
  const wantedMinutes = parseInt(hour) * 60 + parseInt(minute);
  const gotMinutes = parseInt(etHour) * 60 + parseInt(etMinute);
  const minutesDiff = wantedMinutes - gotMinutes;

  // Adjust the date by this difference
  const correctedDate = new Date(desiredETTime);
  correctedDate.setMinutes(correctedDate.getMinutes() + minutesDiff);

  // Also check if the date changed
  const wantedDay = parseInt(day);
  const gotDay = parseInt(etDay);
  if (wantedDay !== gotDay) {
    const daysDiff = wantedDay - gotDay;
    correctedDate.setDate(correctedDate.getDate() + daysDiff);
  }

  // Now get the UTC values
  const utcYear = correctedDate.getUTCFullYear();
  const utcMonth = String(correctedDate.getUTCMonth() + 1).padStart(2, '0');
  const utcDay = String(correctedDate.getUTCDate()).padStart(2, '0');
  const utcHours = String(correctedDate.getUTCHours()).padStart(2, '0');
  const utcMinutes = String(correctedDate.getUTCMinutes()).padStart(2, '0');

  return {
    date: `${utcYear}-${utcMonth}-${utcDay}`,
    time: `${utcHours}:${utcMinutes}`
  };
};

/**
 * Format an event's date and time for display in ET
 * @param {string} dateStr - Date string in YYYY-MM-DD format (UTC)
 * @param {string} timeStr - Time string in HH:MM format (UTC)
 * @returns {string} - Formatted string like "Monday, January 15, 2024"
 */
export const formatEventDateET = (dateStr, timeStr = '00:00') => {
  const converted = convertUTCtoET(dateStr, timeStr);

  if (!converted.displayDate) {
    return formatDate(dateStr);
  }

  return converted.displayDate.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format an event's time for display in ET
 * @param {string} dateStr - Date string in YYYY-MM-DD format (needed for timezone calculation)
 * @param {string} timeStr - Time string in HH:MM format (UTC)
 * @returns {string} - Formatted time in ET
 */
export const formatEventTimeET = (dateStr, timeStr) => {
  const converted = convertUTCtoET(dateStr, timeStr);
  return converted.time;
};

/**
 * What a programme's fee is charged per, as a display suffix.
 *
 * A course sells a term and bills monthly; open play and meetups sell one
 * night, so "$15/mo" on an open play is simply wrong — it is $15 a session.
 * Driven by the resolved offering config rather than programType, so a variant
 * (a course billed per session, say) follows the behaviour and not the label.
 *
 * `long` picks "/month" over "/mo" for the roomier surfaces (flyers, panels).
 */
export function feeUnit(program, { long = false } = {}) {
  return program?.offering?.registrationScope === 'occurrence'
    ? '/session'
    : (long ? '/month' : '/mo');
}
